import MixpanelProvider, { useMixpanelContext } from "contexts/MixpanelContext";
import { useAuthenticator } from "hooks/useAuthenticator";
import { SBBox, SBLeftbar, SBPrivateRoute, SBTopbar } from "modules/Commons";
import { SBGlobalInventoryiesStatusProvider } from "modules/Inventory/components/SBGlobalInventoriesStatus";
import { SBGlobalInventoriesVirtualStatusProvider } from "modules/Inventory/components/SBGlobalInventoriesVirtualStatus";
import { SBGlobalInventoriesDeadStatusProvider } from "modules/Inventory/components/SBGlobalInventoriesDeadStatus";
import SBPurchaseReportModal from "modules/Purchases/components/SBPurchaseReportModal";
import { useEffect, useRef, useState } from "react";
import { Redirect, Route, Switch, useHistory } from "react-router-dom";
import ConfigsView from "views/ConfigsView";
import ForecastModifierView from "views/ForecastViews/ForecastModifierView";
import ForecastModelSelectionView from "views/ForecastViews/ForecastModelSelectionView";
import DataProofView from "views/DataProofView";
import DistributionView from "views/DistributionView";
import ExplorationDashboardView from "views/ExplorationViews/ExplorationDashboardView";
import { FollowUpView } from "views/FollowUpView";
import HomeTableView from "views/HomeViews/HomeTableView";
import HomeView from "views/HomeViews/HomeView";
import { LoginView } from "views/LoginView";
import { NotFoundView } from "views/NotFoundView";
import PurchasePlanningView from "views/PurchaseViews/PurchasePlanningView";
import PurchaseSuggestionsView from "views/PurchaseViews/PurchaseRecommendationView";
import { SimulationView } from "views/SimulationsView/SimulationView";
import ProductModelAnalyserView from "../../ProductModelAnalyserView";
import StatusView from "views/StatusViews/StatusView";
import TransferenceView from "views/TransferencesViews/TransferenceView";
import InventoryPositionView from "views/InventoryPositionView";
import SupplyOrderView from "views/OrderViews/SupplyOrderView";
import SupplyOrderReceivedView from "views/OrderViews/SupplyOrderReceivedView";
import DistributionOrderView from "views/OrderViews/DistributionOrderViews/DistributionOrderView";
import DistributionOrderReceivedView from "views/OrderViews/DistributionOrderViews/DistributionOrderReceivedView";
import TransferenceOrderView from "views/OrderViews/TransferenceOrderViews/TransferenceOrderView";
import TransferenceOrderReceivedView from "views/OrderViews/TransferenceOrderViews/TransferenceOrderReceivedView";
import { SBGlobalInventoryInfoProvider } from "../../../modules/Inventory/components/SBGlobalInventoryInfoModal";
import SystemFilterContext from "contexts/SystemFilterContext";
import NotificationView from "views/NotificationView";
import { useMsal } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";

export const BaseView = () => {
	const auth = useAuthenticator();
	const history = useHistory();
	const { trackLogin } = useMixpanelContext();
	const hasTrackedLogin = useRef(false);

	const { accounts, inProgress } = useMsal();
	const hasTriedCompleteLogin = useRef(false);

	const [isAuthenticating, setIsAuthenticating] = useState(false);
	const [isLoggedIn, setIsLoggedIn] = useState(auth.isLogged());

	useEffect(() => {
		const completeLoginFlow = async () => {
			if (auth.isLogged()) {
				setIsAuthenticating(false);
				setIsLoggedIn(true);
				return;
			}

			if (inProgress !== InteractionStatus.None) {
				setIsAuthenticating(true);
				return;
			}

			if (!accounts.length) {
				setIsAuthenticating(false);
				return;
			}

			if (hasTriedCompleteLogin.current) return;

			hasTriedCompleteLogin.current = true;
			setIsAuthenticating(true);

			try {
				const completed = await auth.completeLogin();

				if (completed) {
					window.location.replace("/home");
					return;
				}

				hasTriedCompleteLogin.current = false;
				setIsAuthenticating(false);
			} catch (error) {
				hasTriedCompleteLogin.current = false;
				setIsAuthenticating(false);
			}
		};

		completeLoginFlow();
	}, [accounts, inProgress]);

	useEffect(() => {
		if (auth.isLogged() && !hasTrackedLogin.current) {
			trackLogin();
			hasTrackedLogin.current = true;
		}

		if (!auth.isLogged()) {
			hasTrackedLogin.current = false;
		}
	}, [auth, trackLogin]);

	const onLogout = async () => {
		await auth.logout();
		setIsLoggedIn(false);
		history.push("/login");
	};

	const onLogin = async (email: string, password?: string) => {
		setIsAuthenticating(true);

		try {
			if (password) {
				await auth.loginWithPassword(email, password);
				setIsLoggedIn(true);
				setIsAuthenticating(false);
			} else {
				await auth.login(email);
			}
		} catch (error) {
			setIsAuthenticating(false);
			throw error;
		}
	};

	const onEnterLogin = () => { };

	if (!isLoggedIn) {
		return (
			<LoginView
				onLogin={onLogin}
				onEnterLogin={onEnterLogin}
				isAuthenticating={isAuthenticating}
			/>
		);
	}

	return (
		<SystemFilterContext>
			<SBGlobalInventoryInfoProvider>
				<SBGlobalInventoryiesStatusProvider>
					<SBGlobalInventoriesVirtualStatusProvider>
						<SBGlobalInventoriesDeadStatusProvider>
							<SBPurchaseReportModal>
								<SBTopbar children={undefined} />
								<SBBox
									display="flex"
									height="100vh"
									pt={10}
									bgcolor="#FAFAFA"
								>
									<SBBox>
										<SBLeftbar onLogout={onLogout} />
									</SBBox>
									<SBBox width="calc(100vw - 9px)">
										<Switch>
											<SBPrivateRoute
												path="/"
												exact
												component={() => <Redirect to="/home" />}
											/>
											<SBPrivateRoute
												path="/followup"
												exact
												component={FollowUpView}
											/>
											<SBPrivateRoute
												path="/purchase/planning"
												exact
												component={PurchasePlanningView}
											/>
											<SBPrivateRoute
												path="/purchase/recomendation"
												exact
												component={PurchaseSuggestionsView}
											/>
											<SBPrivateRoute
												path="/simulation"
												exact
												component={SimulationView}
											/>
											<SBPrivateRoute
												path="/product-model-analyser"
												exact
												component={ProductModelAnalyserView}
											/>
											<SBPrivateRoute
												path="/transference"
												exact
												component={TransferenceView}
											/>
											<SBPrivateRoute
												path="/config"
												exact
												component={ConfigsView}
											/>
											<SBPrivateRoute
												path="/config/forecast/modifier"
												exact
												component={ForecastModifierView}
											/>
											<SBPrivateRoute
												path="/config/forecast/models"
												exact
												component={ForecastModelSelectionView}
											/>
											<SBPrivateRoute
												path="/inventory/position"
												exact
												component={InventoryPositionView}
											/>
											<SBPrivateRoute
												path="/orders/purchases"
												exact
												component={SupplyOrderView}
											/>
											<SBPrivateRoute
												path="/orders/purchases/entregues"
												exact
												component={SupplyOrderReceivedView}
											/>
											<SBPrivateRoute
												path="/orders/distributions"
												exact
												component={DistributionOrderView}
											/>
											<SBPrivateRoute
												path="/orders/distributions/entregues"
												exact
												component={DistributionOrderReceivedView}
											/>
											<SBPrivateRoute
												path="/orders/transferences"
												exact
												component={TransferenceOrderView}
											/>
											<SBPrivateRoute
												path="/orders/transferences/entregues"
												exact
												component={TransferenceOrderReceivedView}
											/>
											<SBPrivateRoute
												path="/status"
												exact
												component={StatusView}
											/>
											<SBPrivateRoute
												path="/home"
												exact
												component={() => <HomeView />}
											/>
											<SBPrivateRoute
												path="/home/details"
												exact
												component={() => <HomeTableView />}
											/>
											<SBPrivateRoute
												path="/distribution"
												exact
												component={DistributionView}
											/>
											<SBPrivateRoute
												path="/exploration"
												exact
												component={ExplorationDashboardView}
											/>
											<SBPrivateRoute
												path="/dataproof"
												exact
												component={DataProofView}
											/>
											<SBPrivateRoute
												path="/notifications"
												exact
												component={NotificationView}
											/>
											<Route
												path="/login"
												component={() => (
													<LoginView
														onLogin={onLogin}
														onEnterLogin={onEnterLogin}
														isAuthenticating={isAuthenticating}
													/>
												)}
											/>
											<SBPrivateRoute
												path="*"
												component={NotFoundView}
											/>
										</Switch>
									</SBBox>
								</SBBox>
							</SBPurchaseReportModal>
						</SBGlobalInventoriesDeadStatusProvider>
					</SBGlobalInventoriesVirtualStatusProvider>
				</SBGlobalInventoryiesStatusProvider>
			</SBGlobalInventoryInfoProvider>
		</SystemFilterContext>
	);
};